import { useState, useEffect, useRef } from 'react'
// context
import Context from './Context'
// service
import authService from './index'
// lodash
import { get } from 'lodash'
// axios
import axios from 'axios'
// firebase
import { onIdTokenChanged } from 'firebase/auth'
import { auth } from '../firebase/app'
import { readTokenClaims } from './firstLoginAccess'
import { fetchUserProfile, requiresReauthentication } from './profileRequest'

const BASE_PATH = process.env.REACT_APP_API_BASEURL

export default function AuthProvider(attr) {
  const [value, setValue] = useState(authService.state)
  const isAuthenticated = get(value, 'user.isAuthenticated', false)
  const token = get(value, 'user.token', null)
  const profileRefresh = value.profileRefresh
  const claimRecoveryStatus = value.claimRecovery?.status
  const [firebaseReady, setFirebaseReady] = useState(false)
  const lastFetchedToken = useRef(null)
  const tokenEventVersion = useRef(0)

  useEffect(() => {
    const unsubscribe = authService.subscribe(setValue)
    authService.evalLogin()
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      const eventVersion = ++tokenEventVersion.current
      const sessionVersion = authService.sessionVersion
      // A forced refresh owns the token + profile transaction. Its Firebase event
      // must not publish the new token before the expected merchant is checked.
      if (authService.state.claimRecovery?.status === 'refreshing' && firebaseUser?.uid === readTokenClaims(authService.state.user.token).sub) return
      if (!firebaseUser) {
        if (get(authService.state, 'user.isAuthenticated') || get(authService.state, 'user.token')) {
          authService.logout({ skipFirebaseSignOut: true, reload: false })
        }
        setFirebaseReady(true)
        return
      }

      try {
        const refreshedToken = await firebaseUser.getIdToken()
        if (auth.currentUser !== firebaseUser || authService.sessionVersion !== sessionVersion || tokenEventVersion.current !== eventVersion) return
        const currentToken = get(authService.state, 'user.token', null)

        if (refreshedToken && refreshedToken !== currentToken) {
          authService.setToken(refreshedToken)
        }
      } catch (error) {
        if (auth.currentUser !== firebaseUser || authService.sessionVersion !== sessionVersion || tokenEventVersion.current !== eventVersion) return
        if (requiresReauthentication(error)) authService.state.claimRecovery = { status: 'reauthenticate', attempted: true }
        else authService.state.profileError = 'No se pudo verificar tu sesión. Volvé a iniciar sesión.'
        authService.setGetting(false)
      } finally {
        setFirebaseReady(true)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (!firebaseReady || !isAuthenticated || !token || claimRecoveryStatus === 'refreshing' || claimRecoveryStatus === 'reauthenticate' || claimRecoveryStatus === 'failed') {
      if (!isAuthenticated) {
        lastFetchedToken.current = null
      }
      return
    }

    const requestKey = `${token}:${profileRefresh}`
    if (authService.state.user.profileLoaded && authService.state.user.profileToken === token && authService.state.user.profileRefreshVersion === profileRefresh) {
      lastFetchedToken.current = requestKey
      return
    }
    if (lastFetchedToken.current === requestKey) {
      return
    }

    lastFetchedToken.current = requestKey
    if (!authService.state.user.profileLoaded) authService.setGetting(true)

    const version = authService.sessionVersion
    const isCurrent = () => authService.sessionVersion === version && authService.state.user.token === token && lastFetchedToken.current === requestKey && authService.state.claimRecovery?.status !== 'refreshing'
    fetchUserProfile(token)
      .then((data) => {
        if (!isCurrent()) return

        authService.setProfile(token, data)

        axios({
          url: `${BASE_PATH}/marketplace/public/categories`,
          method: 'GET',
          headers: { Accept: 'application/json' }
        }).then((response) => {
          if (authService.state.user.token === token) authService.setCategories(Array.isArray(response.data) ? response.data : [])
        }).catch(() => {})
      })
      .catch((error) => {
        if (!isCurrent()) return
        if (error.response?.status === 401) { authService.logout(); return }
        authService.state.profileError = 'No se pudo cargar tu perfil. Reintentá para continuar.'
        authService.setGetting(false)
      })
  }, [firebaseReady, isAuthenticated, token, profileRefresh, claimRecoveryStatus])

  const props = {
    value,
    ...attr,
  }

  return <Context.Provider {...props} />
}
